import { useEffect, useRef } from "react";

export const webApp: TelegramWebApp = window.Telegram.WebApp;

// Tactile feedback. No-ops on desktop/web and older clients (hence try/catch),
// so call sites never need to branch on platform support.
export const haptic = {
  impact(style: "light" | "medium" | "heavy" | "rigid" | "soft" = "light"): void {
    try { webApp.HapticFeedback.impactOccurred(style); } catch { /* unsupported */ }
  },
  selection(): void {
    try { webApp.HapticFeedback.selectionChanged(); } catch { /* unsupported */ }
  },
  success(): void {
    try { webApp.HapticFeedback.notificationOccurred("success"); } catch { /* unsupported */ }
  },
  error(): void {
    try { webApp.HapticFeedback.notificationOccurred("error"); } catch { /* unsupported */ }
  },
};

const INK = "#080808";
const BONE = "#f0ece3";

function brandColors(): { bg: string; fg: string } {
  const scheme = webApp.colorScheme === "light" ? "light" : "dark";
  return scheme === "light"
    ? { bg: BONE, fg: INK }
    : { bg: INK, fg: BONE };
}

export function initWebApp(): void {
  webApp.ready();
  webApp.expand();
  applyThemeVars();
  webApp.onEvent("themeChanged", applyThemeVars);
}

function applyThemeVars(): void {
  const scheme = webApp.colorScheme === "light" ? "light" : "dark";
  document.documentElement.setAttribute("data-theme", scheme);

  const { bg, fg } = brandColors();
  try {
    webApp.setBackgroundColor(bg);
    webApp.setHeaderColor(bg);
    webApp.MainButton.setParams({ color: fg, text_color: bg });
  } catch { /* unavailable in older Telegram versions */ }
}

export function useMainButton(
  text: string,
  enabled: boolean,
  onClick: () => void,
): void {
  const onClickRef = useRef(onClick);
  useEffect(() => { onClickRef.current = onClick; }, [onClick]);

  useEffect(() => {
    const btn = webApp.MainButton;
    const handler = () => { haptic.impact("light"); onClickRef.current(); };
    btn.setText(text);
    try {
      const { bg, fg } = brandColors();
      btn.setParams({ color: fg, text_color: bg });
    } catch { /* older clients */ }
    btn.onClick(handler);
    if (enabled) { btn.show(); btn.enable(); } else { btn.hide(); }
    return () => { btn.offClick(handler); btn.hide(); };
  }, [text, enabled]);
}

export function useBackButton(onBack: () => void, show: boolean = true): void {
  const onBackRef = useRef(onBack);
  useEffect(() => { onBackRef.current = onBack; }, [onBack]);

  useEffect(() => {
    const btn = webApp.BackButton;
    const handler = () => onBackRef.current();
    btn.onClick(handler);
    if (show) btn.show(); else btn.hide();
    return () => { btn.offClick(handler); btn.hide(); };
  }, [show]);
}

export const startParam: string | undefined =
  webApp.initDataUnsafe.start_param ?? undefined;
export const rawInitData: string = webApp.initData;
